import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withModifiers as _withModifiers, withKeys as _withKeys, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["data-modal-name"]
const _hoisted_2 = {
  class: "modal-dialog",
  role: "document"
}
const _hoisted_3 = { class: "modal-content" }
const _hoisted_4 = { class: "modal-header" }
const _hoisted_5 = { class: "modal-title" }
const _hoisted_6 = { class: "modal-body" }
const _hoisted_7 = { class: "modal-footer" }

import { computed, onBeforeUnmount, watch } from 'vue';

interface Props {
  modelValue: boolean;
  title?: string;
  closeOnEsc?: boolean;      // default true
  closeOnBackdrop?: boolean; // default true
  modalName?: string;        // имя модалки для e2e-тестов (data-modal-name)
}

export default /*@__PURE__*/_defineComponent({
  __name: 'BaseModal',
  props: {
    modelValue: { type: Boolean },
    title: {},
    closeOnEsc: { type: Boolean },
    closeOnBackdrop: { type: Boolean },
    modalName: {}
  },
  emits: ["update:modelValue", "ok", "cancel"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const visible = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v),
});

const onOk = () => {
  emit('ok');
  visible.value = false;
};

const onCancel = () => {
  emit('cancel');
  visible.value = false;
};

// ESC-закрытие
const onKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && props.closeOnEsc !== false) {
    onCancel();
  }
};

// Auto-focus при открытии модалки
const setAutoFocus = () => {
  // Находим текущую модалку по modalName или по классу
  const currentModal = document.querySelector(`[data-modal-name="${props.modalName || ''}"]`) || 
                      document.querySelector('.modal.show');
  
  if (!currentModal) return;
  
  // Ищем все элементы, которые могут получить фокус ТОЛЬКО внутри модалки
  // Исключаем кнопки в footer, так как они будут обработаны отдельно в fallback
  const focusableSelectors = [
    '.modal-body button:not([disabled])',
    '.modal-body input:not([disabled])',
    '.modal-body select:not([disabled])',
    '.modal-body textarea:not([disabled])',
    '.modal-body a[href]',
    '.modal-body [tabindex]:not([tabindex="-1"])'
  ].join(', ');
  
  const focusableElements = currentModal.querySelectorAll(focusableSelectors);
  
  if (focusableElements.length === 0) {
    // Если нет фокусируемых элементов, ищем кнопки в footer (actions)
    // Учитываем LoadingButton wrapper - ищем кнопки внутри .modal-footer
    const actionButtons = currentModal.querySelectorAll('.modal-footer button:not([disabled])');
    if (actionButtons.length > 0) {
      // Фокусируемся на последнюю активную кнопку в actions
      (actionButtons[actionButtons.length - 1] as HTMLElement).focus();
    } else {
      // Если нет кнопок в actions, фокусируемся на кнопку закрытия
      const closeButton = currentModal.querySelector('.modal-header .btn-close') as HTMLElement;
      if (closeButton) {
        closeButton.focus();
      }
    }
    return;
  }
  
  // Ищем элементы с data-autofocus
  const autofocusElements = Array.from(focusableElements).filter(el => 
    el.hasAttribute('data-autofocus')
  );
  
  if (autofocusElements.length > 0) {
    // Фокусируемся на первый элемент с data-autofocus
    (autofocusElements[0] as HTMLElement).focus();
  } else {
    // Фокусируемся на первый доступный элемент
    (focusableElements[0] as HTMLElement).focus();
  }
};

watch(
    () => visible.value,
    (val) => {
      if (val) {
        document.body.classList.add('modal-open');
        document.addEventListener('keydown', onKeydown);
        // Устанавливаем auto-focus после следующего тика, чтобы DOM успел обновиться
        setTimeout(setAutoFocus, 0);
      } else {
        document.body.classList.remove('modal-open');
        document.removeEventListener('keydown', onKeydown);
      }
    },
    { immediate: true }
);

onBeforeUnmount(() => {
  document.body.classList.remove('modal-open');
  document.removeEventListener('keydown', onKeydown);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["modal fade", { show: visible.value }]),
    tabindex: "-1",
    role: "dialog",
    style: _normalizeStyle({ display: visible.value ? 'block' : 'none' }),
    "data-modal-name": _ctx.modalName || null,
    onClick: _withModifiers(onCancel, ["self"]),
    onKeyup: _withKeys(onCancel, ["esc"])
  }, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _renderSlot(_ctx.$slots, "header", {}, () => [
            _createElementVNode("h5", _hoisted_5, _toDisplayString(_ctx.title), 1)
          ]),
          _createElementVNode("button", {
            type: "button",
            class: "btn-close",
            "aria-label": "Close",
            onClick: onCancel
          })
        ]),
        _createElementVNode("div", _hoisted_6, [
          _renderSlot(_ctx.$slots, "default")
        ]),
        _createElementVNode("div", _hoisted_7, [
          _renderSlot(_ctx.$slots, "actions", {}, () => [
            _createElementVNode("button", {
              type: "button",
              class: "btn btn-secondary",
              onClick: onCancel
            }, "Отмена"),
            _createElementVNode("button", {
              type: "button",
              class: "btn btn-danger",
              onClick: onOk
            }, "OK")
          ])
        ])
      ])
    ])
  ], 46, _hoisted_1))
}
}

})